import type { OmitKeys, ReplaceProperties } from '../../common/types/util';
import type { StoredFile } from '../../api/files';

export type Bypass = {
  id: number;
  company: string;
  siteName: string;
  siteId: number;
  assetId: number;
  boxId: number;
  siteUuid: number;
  contacts: string;
  subject: string;
  modelNumber: string;
  serialNumber: string;
  expDate: string;
  createdBy: string;
  createdAt: string;
  bypassAction: string;
  bypassType: string;
  creationReason: string;
  systemProcessId: number;
};

export interface PartResponse {
  ids: number[];
  ccn: string;
  partName: string; // Description
  activityId: number;
  activityCrmId: string;
  activityUuid: string;
  partDescription: string;
  assetId: number;
  installationId: number;
  siteId: number;
  assetName: string;
  usedOn: string | null; // Date in string format
  totalQuantity: number;
  usedQuantity: number;
  orderedOn: string | null;
  expectedOn: string;
  partNumber: number; // PartRequestId
  prDestination: string | null;
  purchaseOrderNumber: number;
  poDestination: string | null;
  pickedByUserId: string;
  pickedOn: string;
  pickedQuantity: number;
  currentLocation: string;
  status: 'Ordered' | 'Picked' | 'Received' | 'Staged' | 'Transit' | 'Ready' | 'Transfer'; // Ready replaces Received and Staged Leaving in for Backwards compatibility
  trackingNumber: string;
  vendorName: string;
  activityStatus: string;
  partStatus: string;
  surplusReason: string | null;
  sentTo: string | null;
}

export type Part = ReplaceProperties<
  PartResponse,
  {
    expectedOn: Date | null;
    orderedOn: Date | null;
    usedOn: Date | null;
    // pickedOn: Date | null;
  }
>;

export type ItemInventoryResponse = {
  id: number;
  ccn: string;
  partName: string;
  price: number;
  altPart?: string;
  lastUsedOn: string | null;
  quantity: number;
};

export type ItemInventory = ReplaceProperties<
  ItemInventoryResponse,
  {
    lastUsedOn: Date | null;
  }
>;

enum InvoiceType {
  Open = 'Open',
  PastDue = 'Past Due',
  Paid = 'Paid'
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface InvoiceResponse {
  Status: InvoiceType;
  InvoiceId: number;
  WorkOrderId: number;
  AssetId: number;
  SiteId: number;
  InstallationId: number;
  AssetName: string;
  InvoiceType: string;
  Customer: string;
  Description: string | null;
  Value: number;
  CreatedOn: string; // Date in string format
  DueOn: string; // Date in string format
}

export type Invoice = ReplaceProperties<
  InvoiceResponse,
  {
    CreatedOn: Date;
    DueOn: Date;
  }
>;
/* eslint-enable @typescript-eslint/naming-convention */
export interface SampleDataResponse {
  id: number;
  sample: Sample;
  sampleHours: number;
  barCodeId: string;
  action: string;
  assetId: number | null; // is null for unlinked samples
  issues: string | null;
  siteId: number;
  externalId: number;
  loadHours: number;
  bearingMetals: number;
  coolerMetals: number;
  oxidation: number;
  filter: number;
  ph: number;
  tan: number;
  viscosityDeviations: number;
  viscosity: number;
  recommendations: string;
  assetName: string;
  siteName: string;
  sampleDate: string; // Date in string format
  assetModel: string;
  assetSerial: string;
  reviewed: boolean;
  customerName: string;
}

export type SampleData = ReplaceProperties<
  SampleDataResponse,
  {
    sampleDate: Date;
  }
>;

export interface SampleResponse {
  id: number;
  barcodeId: string;
  sampleData?: SampleDataResponse;
  assetId: number;
  siteId?: number;
  activityId: number;
  activityCrmId: string;
  processingStatus: 'Pending' | 'Received' | 'Imported' | 'ManuallyCleared';
  reviewed: boolean;
  reviewDescription: string | null;
  reviewOutcome: string | null;
  scannedAt: string | null;
  siteName: string;
  customerName: string;
  assetName: string;
  hasSufficientData: boolean;
  sampleDataCount: number;
  receivedAt: string | null;
  importedAt: string | null;
}

export type Sample = ReplaceProperties<
  SampleResponse,
  {
    scannedAt: Date | null;
    receivedAt: Date | null;
    importedAt: Date | null;
    sampleData: SampleData | null;
  }
>;

export enum EUserGroups {
  Support = 'Support',
  Orders = 'Orders',
  Accounting = 'Accounting',
  Sales = 'Sales'
}

export interface FlagResponse {
  id: number;
  activityCrmId: string;
  status: string;
  activityId: string;
  createdAt: string;
  createdByUserName: string;
  createdById: number;
  reason: string;
  respondedAt: string | null;
  respondedByUserName: string | null;
  respondedById: number | null;

  response: string | null;
  clearedAt: string | null;
  claimedById: number | null;
  claimedAt: string | null;
  claimedByUserName: string | null;
  flagRecipientGroup: EUserGroups;
  siteName: string | null;
}

export type Flag = ReplaceProperties<
  FlagResponse,
  {
    createdAt: Date;
    respondedAt: Date | null;
    clearedAt: Date | null;
    claimedAt: Date | null;
  }
>;

export interface ActivityExpenseResponse {
  id: string;
  activityId: string | null;
  description: string;
  amount: number;
  saleTaxAmount: number;
  vendorName: string;
  paymentType: string;
  createdAt: string;
  receipt: StoredFile | null;
  canEdit: boolean;
}

export type ActivityExpense = ReplaceProperties<
  ActivityExpenseResponse,
  {
    createdAt: Date;
  }
>;

export interface CreateActivityExpenseRequest {
  id?: string;
  description: string;
  amount: number;
  saleTaxAmount: number;
  vendorName: string;
  paymentType: string;
  expenseTypeId?: number | null;
}

export interface UpdateActivityExpenseRequest {
  description: string;
  amount: number;
  saleTaxAmount: number;
  vendorName: string;
  paymentType: string;
}

export interface GetCustomerAndSiteAssetsRequest {
  customerId?: number;
  siteId?: number;
  assetId?: number;
}

export interface GetAssignedToResponse {
  id: number;
  name: string;
}

export interface ActivityTimeResponse {
  activityId: string;
  entryDate: string; // Date in string format
  startTime: string; // Date in string format
  endTime: string; // Date in string format
  type: string;
  totalTime: string; // Date in string format
  userId: string;
}

export interface UpdateActivityDebriefRequest {
  activityId: string;
  scope: string;
  usedParts: Part[];
  assetHours: number | null;
  unknownAssetHourReason: string | null;
  additionalReviewRequired: boolean;
  timeZone: string;
}

export interface AssetWithDetailsResponse {
  id: number;
  assetCrmId: string | undefined;
  assetName: string;
  companyName?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  siteName?: string | null;
  description: string | null;
  assetManufacturer: string | null;
  crmType: string | null;
  crmType2: string | null;
  modelNumber: string;
  serialNumber: string;
  location: string | null;
  assetGroup: string | null;
}

// How would we like to interact with this
// edge cases: may care about the user Role, may care about the number of times the user has interacted with the guide
export interface UserOnboardingInteraction {
  interactionId?: number;
  guideId: number;
  title: string;
  isActive: boolean;
  currentStep: number;
  hasCompleted: boolean;
}

export function getAssetDetailsLabel(asset: OmitKeys<AssetWithDetailsResponse, 'description'>) {
  const formattedAssetDetails = asset.crmType
    ? `Type: ${asset.crmType2}, MFG: ${asset.crmType}, Model: ${asset.modelNumber}, Serial: ${asset.serialNumber}`
    : asset.assetGroup
      ? `${asset.assetGroup} , Type: ${asset.assetManufacturer} Model: ${asset.modelNumber}, Serial: ${asset.assetName}`
      : `Type: N/A, Model: ${asset.modelNumber}, Serial: ${asset.serialNumber}`;
  //  `Asset: ${asset.assetName}`;

  const namePart = asset.assetName ? `Name: ${asset.assetName}, ` : '';
  const locationPart = asset.location ? `Location: ${asset.location}, ` : '';
  return `Id: ${asset.assetCrmId ?? 'N/A'} , ${namePart}${locationPart}${formattedAssetDetails}`;
}

export interface AddInteractionRequest {
  guideId: number;
  step?: number;
  isCompleted: boolean;
}

// Contacts

export const HardCodedValues = {
  SlamSchemaId: 1
} as const;
